import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import useEnhancedFormik from '../../hooks/useEnhancedFormik'
import { STEPS } from '../../common/constants'
import { shippingSendCustomShipmentPropTypes } from '../../common/propTypes'
import { nullablePropType } from '../../common/propTypes'
import {
  makeFieldErrorsPropType,
  makeValuesPropType,
} from '../../common/formikPropTypes'
import { generateTouchedObject } from '../../libs/formik'
import ConfirmationWithErrorModal from '../../ui/modals/confirmationWithError'
import ActionButtons from '../../ui/views/shippingSend/ActionButtons'
import ShippingSendForm from '../../ui/views/shippingSend/FormWrapper'
import StepCustomsUI from '../../ui/views/shippingSend/StepCustoms'

import useShippingSendStep from './useShippingSendStep'
import { generateRandomKey } from '../../utils'

const CustomSchema = destinCountryCode =>
  Yup.object().shape({
    wasSubmit: Yup.bool(),
    senderType: Yup.string().required(),
    receptorType: Yup.string().required(),
    senderInfo: Yup.object().when('senderType', {
      is: 'PERSONAL_NATIONAL',
      then: Yup.object().shape({
        dni: Yup.string().nullable().required(),
        reason: Yup.string().nullable().required(),
      }),
      otherwise: Yup.object().shape({
        companyName: Yup.string().nullable().required(),
        nifIva: Yup.string().nullable().required(),
        reason: Yup.string().nullable().required(),
        bill: Yup.string().nullable().max(20).required(),
      }),
    }),
    receptorInfo: Yup.object().when('receptorType', {
      is: 'PERSONAL_NATIONAL',
      then: Yup.object().shape({
        dni: Yup.string().nullable().required(),
      }),
      otherwise: Yup.object().shape({
        companyName: Yup.string().nullable().required(),
        nifIva: Yup.string().nullable().required(),
      }),
    }),
    inventory: Yup.array().of(
      Yup.object()
        .shape({
          category: Yup.object().shape({
            commodity_taric_code: Yup.number().when('commodity_type_code', {
              is: commodity_type_code => !!commodity_type_code,
              then: Yup.number().nullable(),
              otherwise: Yup.number().test(
                'is-not-empty',
                { key: 'shipping-send-form:customs:taric-code-empty' },
                value =>
                  value !== undefined &&
                  value !== null &&
                  value.toString().trim() !== '',
              ),
            }),

            commodity_taric_code_other: Yup.number().when(
              'commodity_taric_code',
              {
                is: commodityTaricCode =>
                  destinCountryCode === 'ES' ||
                  (destinCountryCode !== 'ES' && !!commodityTaricCode),
                then: Yup.number()
                  .nullable()
                  .test(
                    'has-decimal',
                    {
                      key: 'shipping-send-form:customs:article-number-format-error',
                    },
                    value => Number.isInteger(value) || !value,
                  )
                  .test(
                    'is-eigth-digits',
                    { key: 'shipping-send-form:customs:taric-code-eigth' },
                    value => (value ? value.toString().length >= 8 : true),
                  ),
                otherwise: Yup.number()
                  .test(
                    'is-not-empty',
                    { key: 'shipping-send-form:customs:taric-code-empty' },
                    value =>
                      value !== undefined &&
                      value !== null &&
                      value.toString().trim() !== '',
                  )
                  .test(
                    'has-decimal',
                    {
                      key: 'shipping-send-form:customs:article-number-format-error',
                    },
                    value => Number.isInteger(value),
                  )
                  .test(
                    'is-eigth-digits',
                    { key: 'shipping-send-form:customs:taric-code-eigth' },
                    value => (value ? value.toString().length >= 8 : true),
                  )
                  .required(),
              },
            ),
          }).nullable().required(),
          place: Yup.object().nullable().required(),
          description: Yup.string()
            .min(1)
            .max(70, { key: 'error:customs-description:max-value' })
            .required(),
          quantity: Yup.number()
            .test(
              'has-decimal',
              { key: 'shipping-send-form:customs:article-number-format-error' },
              value => Number.isInteger(value),
            )
            .min(1, { key: 'error:customs:min-value' })
            .max(9999999, { key: 'error:customs:max-value' })
            .required(),
          price: Yup.string()
            .test(
              'price',
              { key: 'shipping-send-form:new-address:phone-format-error' },
              value => {
                if (!value) return false
                const sanitizedValue = value.replace(/,/g, '')
                return (
                  !isNaN(sanitizedValue) &&
                  parseFloat(sanitizedValue) >= 1 &&
                  parseFloat(sanitizedValue) <= 9999999
                )
              },
            )
            .required(),
        })
        .required(),
    ),
    additionalDocumentation: Yup.array().of(Yup.object().shape({
      file: Yup.object().nullable(),
      fileBodyPost: Yup.object().nullable(),
      id: Yup.string().nullable(),
      type: Yup.string().required(),
    })),
    readed: Yup.boolean().test(value => value),
    authorized: Yup.boolean().test(value => value),
  })

const INITIAL_VALUES = {
  wasSubmit: false,
  senderType: 'BUSINESS_UNION',
  receptorType: 'PERSONAL_NATIONAL',
  senderInfo: {
    dni: null,
    reason: null,
    companyName: null,
    nifIva: null,
    bill: null,
  },
  receptorInfo: {
    dni: null,
    companyName: null,
    nifIva: null,
  },
  inventory: [
    {
      key: generateRandomKey(),
      category: null,
      place: null,
      description: '',
      quantity: 1,
      price: 1,
    },
  ],
  additionalDocumentation: [],
  readed: false,
  authorized: false,
}

const StepCustoms = ({
  hasCustom,
  destinCountryCode,
  originCountryCode,
  savedValues,
  activeStep,
  serverFieldErrors,
  hasOtherError,
  onSubmitSuccess,
  onCancel,
  centreCode,
  onSetDirty,
  hasConfirmation,
  onCancelConfirmation,
  onAcceptConfirmation,
  allowsElectronicalTradeDocuments,
}) => {
  const formik = useFormik({
    validateOnChange: true,
    validationSchema: CustomSchema(destinCountryCode),
    initialValues: savedValues ?? INITIAL_VALUES,
    initialTouched: !!savedValues
      ? generateTouchedObject(shippingSendCustomShipmentPropTypes, savedValues)
      : undefined,
    onSubmit: onSubmitSuccess,
  })
  
  const enhancedFormik = useEnhancedFormik(
    shippingSendCustomShipmentPropTypes,
    formik,
    { fieldErrors: serverFieldErrors },
  )

  const { handleDiscardConfirmation, hasServerError, hasAnyError } =
    useShippingSendStep({
      formik,
      enhancedFormik,
      onSetDirty,
      hasConfirmation,
      onAcceptConfirmation,
      serverFieldErrors,
      propTypes: shippingSendCustomShipmentPropTypes,
    })

  useEffect(() => {
    if (hasConfirmation && !hasAnyError) {
      if (!formik.values?.wasSubmit)
        enhancedFormik.handleChange({
          target: { name: 'wasSubmit', value: true },
        })
      onSubmitSuccess(formik.values)
    }
  }, [formik.values, hasAnyError, hasConfirmation, onSubmitSuccess])

  const handleAddItem = async () => {
    await formik.setValues(
      values => ({
        ...values,
        inventory: [
          ...values.inventory,
          {
            key: generateRandomKey(),
            category: null,
            place: null,
            description: '',
            quantity: 1,
            price: 1,
          },
        ],
      }),
      false,
    )
  }

  const handleRemoveItem = async index => {
    const newInventory = [...formik.values.inventory]
    newInventory.splice(index, 1)
    await formik.setValues(values => ({
      ...values,
      inventory: newInventory,
    }))
  }

  const handleAddDocument = async document => {
    await formik.setValues(
      values => ({
        ...values,
        additionalDocumentation: [
          ...values.additionalDocumentation,
          ...document,
        ],
      }),
      false,
    )
  }

  const handleRemoveDocument = async id => {
    const newAdditionalDocumentation = [
      ...formik.values.additionalDocumentation,
    ]
    const index = newAdditionalDocumentation.findIndex(
      element => element.id === id,
    )

    newAdditionalDocumentation.splice(index, 1)
    await formik.setValues(values => ({
      ...values,
      additionalDocumentation: newAdditionalDocumentation,
    }))
  }

  const handleChangeDocument = async data => {
    const newAdditionalDocumentation = [
      ...formik.values.additionalDocumentation,
    ]
    const index = newAdditionalDocumentation.findIndex(
      element => element.id === data?.id,
    )

    newAdditionalDocumentation[index] = data
    await formik.setValues(values => ({
      ...values,
      additionalDocumentation: newAdditionalDocumentation,
    }))
  }
  return (
    <ShippingSendForm onSubmit={enhancedFormik.handleSubmit}>
      {/* TODO: pasar el nombre de la documentación, su tipo y su path */}
      <StepCustomsUI
        values={formik.values}
        destinCountryCode={destinCountryCode}
        originCountryCode={originCountryCode}
        handleRemoveItem={handleRemoveItem}
        handleAddItem={handleAddItem}
        handleAddDocument={handleAddDocument}
        handleRemoveDocument={handleRemoveDocument}
        handleChangeDocument={handleChangeDocument}
        touched={formik.touched}
        centreCode={centreCode}
        fieldErrors={enhancedFormik.fieldErrors}
        formErrors={enhancedFormik.formErrors}
        onChange={enhancedFormik.handleChange}
        onBlur={formik.handleBlur}
        hasCustoms={hasCustom}
        allowsElectronicalTradeDocuments={allowsElectronicalTradeDocuments}
      />

      <ActionButtons
        isPrimaryDisabled={
          (formik.touched.wasSubmit &&
            Object.keys(enhancedFormik.fieldErrors).length) ||
          (allowsElectronicalTradeDocuments &&
            (formik.values.additionalDocumentation.some(
              file => file?.type === null,
            ) ||
              formik.values.additionalDocumentation.some(
                file => file?.error,
              )) &&
            hasCustom)
        }
        isDirty={formik.dirty}
        isSaved={savedValues !== null}
        onCancel={onCancel}
        hasOtherError={hasOtherError}
        hasServerError={hasServerError}
        currentStep={STEPS.CUSTOMS}
        activeStep={activeStep}
      />

      {hasConfirmation && hasAnyError ? (
        <ConfirmationWithErrorModal
          onDiscard={handleDiscardConfirmation}
          onAmend={onCancelConfirmation}
        />
      ) : null}
    </ShippingSendForm>
  )
}

StepCustoms.propTypes = {
  savedValues: nullablePropType(
    makeValuesPropType(shippingSendCustomShipmentPropTypes).isRequired,
  ),
  activeStep: PropTypes.oneOf(Object.values(STEPS)).isRequired,
  serverFieldErrors: nullablePropType(
    makeFieldErrorsPropType(shippingSendCustomShipmentPropTypes).isRequired,
  ),
  hasOtherError: PropTypes.bool.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSetDirty: PropTypes.func.isRequired,
  hasConfirmation: PropTypes.bool.isRequired,
  onCancelConfirmation: PropTypes.func.isRequired,
  onAcceptConfirmation: PropTypes.func.isRequired,
}

export default StepCustoms
